import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getCurrencySymbol } from '../lib/currency';

import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { DatePickerField } from '../components/ui/date-picker-field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import logger from '../lib/logger';
import {
  Save, Send, ArrowLeft, FileText, Calendar, DollarSign, Shield,
  Plus, Trash2, CheckCircle2, XCircle, Clock, AlertTriangle, Users,
  ChevronRight, ChevronDown, Loader2, Building2, Target, Download,
  MailOpen, MousePointerClick, Mail, MailWarning, MailX, Lock, ExternalLink,
  ShieldCheck, ShieldAlert, RefreshCw, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Maps the Resend webhook's per-send status → a compact chip for the
// Send-History row. Status comes from `sow_sends.status` which is kept
// in sync by the `/api/webhooks/resend` receiver.
const SEND_STATUS_CONFIG = {
  sent:              { label: 'Sent',      color: 'bg-slate-100 text-slate-700 border-slate-200',   icon: Send },
  delivery_delayed:  { label: 'Delayed',   color: 'bg-amber-50 text-amber-700 border-amber-200',    icon: Clock },
  delivered:         { label: 'Delivered', color: 'bg-blue-50 text-blue-700 border-blue-200',       icon: Mail },
  opened:            { label: 'Opened',    color: 'bg-teal-50 text-teal-700 border-teal-200',       icon: MailOpen },
  clicked:           { label: 'Clicked',   color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: MousePointerClick },
  bounced:           { label: 'Bounced',   color: 'bg-rose-50 text-rose-700 border-rose-200',        icon: MailX },
  complained:        { label: 'Spam',      color: 'bg-rose-100 text-rose-800 border-rose-300',       icon: MailWarning },
};

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700', icon: FileText },
  pending_cco: { label: 'Pending CCO Approval', color: 'bg-amber-100 text-amber-700', icon: Clock },
  cco_approved: { label: 'CCO Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  cco_rejected: { label: 'Changes Requested', color: 'bg-red-100 text-red-700', icon: XCircle },
  sent_to_client: { label: 'Sent to Client', color: 'bg-blue-100 text-blue-700', icon: Send },
  client_signed: { label: 'Client Signed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
};

const SOW_SECTIONS = [
  { id: 'overview', label: 'Overview', icon: Target },
  { id: 'integrations', label: 'Integrations', icon: Shield },
  { id: 'migration', label: 'Data Migration', icon: FileText },
  { id: 'payment', label: 'Payment Terms', icon: DollarSign },
  { id: 'assumptions', label: 'Assumptions', icon: AlertTriangle },
  { id: 'signatories', label: 'Signatories', icon: Users },
];

export default function SOWEditorPage() {
  const { sowId } = useParams();
  const [searchParams] = useSearchParams();
  const estimateId = searchParams.get('estimate');
  const navigate = useNavigate();
  const { user } = useAuth();

  const [sow, setSow] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [integrationTypes, setIntegrationTypes] = useState([]);

  // Form state
  const [form, setForm] = useState({
    customer_goal: '',
    goal_inherited_from_conditions: false,
    start_date: '',
    go_live_date: '',
    customer_legal_name: '',
    sow_effective_date: '',
    contracting_entity_id: '',
    data_migration: {
      source: '', date: '', data_format: '',
      organizations: false, users: false, content_library: false,
      learner_history: false, certifications: false,
    },
    payment_terms: {
      total_fee: 0, total_hours: 0,
      schedule_type: '50_50', schedule_details: '',
      include_milestone_schedule: true,
    },
    assumptions: [],
    out_of_scope: [],
    optional_addons: [],
    customer_signatory: { name: '', title: '' },
    seertech_signatory: { name: '', title: '' },
    hypercare_duration_days_override: null,
  });

  const [seertechEntities, setSeertechEntities] = useState([]);
  const [organization, setOrganization] = useState(null);

  const isReadOnly = sow?.status === 'sent_to_client' || sow?.status === 'client_signed';

  // v377 — SOW currency lock / re-sync / revert (price fixed at a point in time).
  const doCurrencyAction = async (action, payload) => {
    if (!sow?.sow_id) return;
    try {
      if (action === 'lock') {
        await axios.put(`${API_URL}/api/sow/${sow.sow_id}/currency-lock`, { locked: payload }, { withCredentials: true });
        toast.success(payload ? 'Payment currency locked — estimate changes will no longer auto-sync' : 'Currency unlocked — auto-sync re-enabled');
      } else if (action === 'resync') {
        const res = await axios.post(`${API_URL}/api/sow/${sow.sow_id}/currency-resync`, {}, { withCredentials: true });
        toast.success(res.data?.changed ? `Payment currency re-synced to ${res.data.currency}` : 'Already in sync with the estimate');
      } else if (action === 'revert') {
        const res = await axios.post(`${API_URL}/api/sow/${sow.sow_id}/currency-revert`, {}, { withCredentials: true });
        toast.success(`Payment currency reverted to ${res.data?.currency}`);
      }
      const refreshed = await axios.get(`${API_URL}/api/sow/${sow.sow_id}`, { withCredentials: true });
      setSow(refreshed.data);
      setForm(prev => ({ ...prev, payment_terms: refreshed.data.payment_terms || prev.payment_terms }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Currency action failed');
    }
  };
  const canSubmit = sow?.status === 'draft' || sow?.status === 'cco_rejected';
  const canApprove = ['cco', 'super_admin'].includes(user?.role) && sow?.status === 'pending_cco';
  const canSendToClient = sow?.status === 'cco_approved';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [typesRes, entitiesRes] = await Promise.all([
        axios.get(`${API_URL}/api/sow/integration-types`),
        axios.get(`${API_URL}/api/settings/seertech-entities`).catch(() => ({ data: { entities: [] } })),
      ]);
      setIntegrationTypes(typesRes.data.integration_types || []);
      const entities = entitiesRes.data?.entities || [];
      setSeertechEntities(entities);
      // Preferred entity precedence (highest wins):
      //   1. User's saved default (`user.preferences.default_contracting_entity_id`)
      //      — but only when that id still exists in the active entities list.
      //   2. The entity flagged `is_default` by the Super Admin.
      //   3. First entity in the list.
      const preferredId = user?.preferences?.default_contracting_entity_id;
      const userDefault = preferredId ? entities.find(e => e.id === preferredId) : null;
      const defaultEntity = userDefault
        || entities.find(e => e.is_default)
        || entities[0]
        || null;

      if (sowId && sowId !== 'new') {
        const sowRes = await axios.get(`${API_URL}/api/sow/${sowId}`);
        setSow(sowRes.data);
        setForm({
          customer_goal: sowRes.data.customer_goal || '',
          goal_inherited_from_conditions: !!sowRes.data.goal_inherited_from_conditions,
          start_date: sowRes.data.start_date || '',
          go_live_date: sowRes.data.go_live_date || '',
          customer_legal_name: sowRes.data.customer_legal_name || '',
          sow_effective_date: sowRes.data.sow_effective_date || '',
          contracting_entity_id: sowRes.data.contracting_entity_id || (defaultEntity?.id || ''),
          data_migration: sowRes.data.data_migration || form.data_migration,
          payment_terms: sowRes.data.payment_terms || form.payment_terms,
          assumptions: sowRes.data.assumptions || [],
          out_of_scope: sowRes.data.out_of_scope || [],
          optional_addons: sowRes.data.optional_addons || [],
          customer_signatory: sowRes.data.customer_signatory || { name: '', title: '' },
          seertech_signatory: sowRes.data.seertech_signatory || { name: '', title: '' },
          hypercare_duration_days_override: sowRes.data.hypercare_duration_days_override ?? null,
        });
        // Fetch linked estimate
        if (sowRes.data.estimate_id) {
          try {
            const estRes = await axios.get(`${API_URL}/api/estimates/${sowRes.data.estimate_id}`);
            setEstimate(estRes.data);
            // Backfill total_hours on existing SOWs that were saved before the
            // frontend knew to source from results.total_effort.
            const estHours = estRes.data?.results?.total_effort || 0;
            if (estHours && !(sowRes.data.payment_terms?.total_hours)) {
              setForm(prev => ({
                ...prev,
                payment_terms: { ...prev.payment_terms, total_hours: estHours },
              }));
            }
            // Hydrate SOW Project Timeline from Project Conditions when the SOW
            // hasn't yet captured its own dates. Conditions are the new source
            // of truth, so any blank Start / Go-Live should default to whatever
            // the Estimator captured. Saved overrides on the SOW are respected.
            if (estRes.data?.has_conditions && estRes.data?.project_conditions) {
              const pc = estRes.data.project_conditions;
              setForm(prev => ({
                ...prev,
                start_date: prev.start_date || pc.goal_start_date || '',
                go_live_date: prev.go_live_date || pc.goal_go_live_date || '',
              }));
            }
            // Fetch the linked Organization so the live preamble preview can
            // use the same legal_name precedence the backend applies at export.
            if (estRes.data?.org_id) {
              try {
                const orgRes = await axios.get(`${API_URL}/api/organizations/${estRes.data.org_id}`);
                setOrganization(orgRes.data);
              } catch {
                setOrganization(null);
              }
            }
          } catch (err) { logger.error('Failed to load linked estimate:', err); }
        }
      } else if (estimateId) {
        // Creating new SOW — check if one already exists
        try {
          const existingRes = await axios.get(`${API_URL}/api/sow/by-estimate/${estimateId}`);
          // SOW exists, redirect to it
          navigate(`/sow/${existingRes.data.sow_id}`, { replace: true });
          return;
        } catch {
          // No existing SOW, fetch estimate to pre-populate
          const estRes = await axios.get(`${API_URL}/api/estimates/${estimateId}`);
          setEstimate(estRes.data);
          if (estRes.data?.org_id) {
            try {
              const orgRes = await axios.get(`${API_URL}/api/organizations/${estRes.data.org_id}`);
              setOrganization(orgRes.data);
            } catch {
              setOrganization(null);
            }
          }
          const results = estRes.data.results || {};
          // Prefill Start / Go-Live dates from Project Conditions when present.
          // Mirrors the new backend default applied on POST /api/sow so the
          // user sees the inherited values in the form before they save.
          const pc = (estRes.data?.has_conditions && estRes.data?.project_conditions) || {};
          setForm(prev => ({
            ...prev,
            customer_legal_name: estRes.data.organization_name || '',
            contracting_entity_id: defaultEntity?.id || '',
            start_date: prev.start_date || pc.goal_start_date || '',
            go_live_date: prev.go_live_date || pc.goal_go_live_date || '',
            payment_terms: {
              ...prev.payment_terms,
              total_fee: results.total_cost || 0,
              total_hours: results.total_effort || 0,
              currency: prev.payment_terms?.currency || estRes.data?.currency_code || 'USD',
            },
          }));
        }
      }
    } catch (err) {
      toast.error('Failed to load SOW data');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sowId, estimateId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (sow) {
        await axios.put(`${API_URL}/api/sow/${sow.sow_id}`, form);
        toast.success('SOW saved');
        fetchData();
      } else if (estimateId) {
        const res = await axios.post(`${API_URL}/api/sow`, { ...form, estimate_id: estimateId });
        toast.success('SOW created');
        navigate(`/sow/${res.data.sow_id}`, { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save SOW');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!sow) return;
    try {
      await axios.post(`${API_URL}/api/sow/${sow.sow_id}/submit-for-approval`);
      toast.success('SOW submitted for CCO approval');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    }
  };

  const handleApprove = async () => {
    if (!sow) return;
    try {
      await axios.post(`${API_URL}/api/sow/${sow.sow_id}/approve`, { comments: '' });
      toast.success('SOW approved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    const comments = window.prompt('Reason for requesting changes:');
    if (comments === null) return;
    try {
      await axios.post(`${API_URL}/api/sow/${sow.sow_id}/reject`, { comments });
      toast.success('Changes requested');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
  };

  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendRecipientEmail, setSendRecipientEmail] = useState('');
  const [sendSubject, setSendSubject] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [sending, setSending] = useState(false);

  const [sendHistory, setSendHistory] = useState([]);
  const [resendingId, setResendingId] = useState(null);
  // Live deliverability tracking — polls the per-SOW event timeline
  // every 30s so CROs see "Delivered → Opened → Clicked" transitions
  // without reloading the editor.
  const [serverNowCheckpoint, setServerNowCheckpoint] = useState(null);
  const [unreadEventDelta, setUnreadEventDelta] = useState(0);
  const [expandedSendId, setExpandedSendId] = useState(null);
  // Tracks which (send_id, event_type) pairs we've already surfaced as
  // a bounce/complaint toast so a later poll doesn't re-nag the CRO.
  // Ref-backed so updating it doesn't re-render the editor.
  const toastedDeliverabilityRef = useRef(new Set());

  const fetchSendHistory = useCallback(async () => {
    if (!sow?.sow_id) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/sow/${sow.sow_id}/send-history?limit=5`,
      );
      setSendHistory(res.data?.sends || []);
    } catch (err) {
      logger.warn('Failed to load send history', err);
    }
  }, [sow?.sow_id]);
  useEffect(() => { fetchSendHistory(); }, [fetchSendHistory]);

  // Lightweight template-presence check for the header chip — confirms
  // SOW exports will render with the modern Seertech template (not the
  // legacy fallback). Cheap (no rendering, just a stat() call) so we
  // fetch on mount and let the user re-verify with a click.
  const [templateInfo, setTemplateInfo] = useState(null);
  const fetchTemplateInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/sow/template/info`, { withCredentials: true });
      setTemplateInfo(res.data);
    } catch {
      setTemplateInfo({ exists: false, ok: false });
    }
  }, []);
  useEffect(() => { fetchTemplateInfo(); }, [fetchTemplateInfo]);

  // Sync the SOW's start_date / go_live_date to the parent estimate's
  // current Project Conditions. Used when the underlying Conditions
  // were updated after this SOW was created and the dates have drifted.
  const [syncingDates, setSyncingDates] = useState(false);
  const handleSyncConditionsDates = useCallback(async () => {
    if (!sow?.sow_id) return;
    setSyncingDates(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/sow/${sow.sow_id}/sync-conditions-dates`,
        {},
        { withCredentials: true },
      );
      toast.success('SOW dates synced to current Project Conditions');
      // Reflect server truth in the form locally so the inputs update
      // without an extra GET round-trip.
      setForm(prev => ({
        ...prev,
        start_date: res.data?.start_date || '',
        go_live_date: res.data?.go_live_date || '',
      }));
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to sync dates');
    } finally {
      setSyncingDates(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sow?.sow_id]);

  // Seed the event-timeline checkpoint so polling only counts genuinely
  // new events received after this moment.
  const seedEventCheckpoint = useCallback(async () => {
    if (!sow?.sow_id) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/sow/${sow.sow_id}/send-history/events`,
      );
      setServerNowCheckpoint(res.data?.server_now || null);
      setUnreadEventDelta(0);
    } catch (err) {
      logger.warn('Failed to seed event checkpoint', err);
    }
  }, [sow?.sow_id]);
  useEffect(() => { seedEventCheckpoint(); }, [seedEventCheckpoint]);

  // Opens the Send dialog pre-filled for a retry after a bounce /
  // complaint. Leaves the recipient field EMPTY so the CRO has to type
  // the corrected address — a small forcing function against
  // re-sending to the exact same bad mailbox. Declared before the
  // poll effect so the effect's deps can reference it cleanly.
  const openSendDialogForRetry = useCallback((bouncedRecipient) => {
    setSendRecipientEmail('');
    setSendSubject(
      sow?.customer_legal_name
        ? `Statement of Work — ${sow.customer_legal_name}`
        : 'Statement of Work',
    );
    setSendMessage(
      bouncedRecipient
        ? `Resending — the previous delivery to ${bouncedRecipient} didn't reach you. Please let me know if this updated address works.`
        : '',
    );
    setSendDialogOpen(true);
  }, [sow?.customer_legal_name]);

  // Poll every 30s for new events. Only the delta since the last
  // checkpoint is returned; unreadEventDelta drives the "N new events"
  // pill next to the Send History heading.
  useEffect(() => {
    if (!sow?.sow_id || !serverNowCheckpoint) return undefined;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/sow/${sow.sow_id}/send-history/events`,
          { params: { since: serverNowCheckpoint } },
        );
        const newEvents = res.data?.events || [];
        if (newEvents.length > 0) {
          setUnreadEventDelta((prev) => prev + newEvents.length);
          // Also refresh the send records so each row's status chip
          // and embedded `events[]` list update in-place.
          fetchSendHistory();

          // Proactive toast: surface bounces / complaints right away
          // so the CRO can react without spotting the pill. Deduped
          // per (send_id, event_type) pair for this session.
          const deliverabilityAlerts = newEvents.filter(
            (ev) => ev.type === 'email.bounced' || ev.type === 'email.complained',
          );
          deliverabilityAlerts.forEach((ev) => {
            const dedupKey = `${ev.send_id}::${ev.type}`;
            if (toastedDeliverabilityRef.current.has(dedupKey)) return;
            toastedDeliverabilityRef.current.add(dedupKey);
            const verb = ev.type === 'email.bounced' ? 'Bounced' : 'Marked as spam';
            const recipient = ev.recipient_email || 'recipient';
            toast.error(`⚠️ ${verb} — ${recipient}`, {
              id: `deliverability-${dedupKey}`,
              description: 'The last send didn\'t reach the client. Retry with a corrected address?',
              duration: 15000,
              action: {
                label: 'Resend to…',
                onClick: () => openSendDialogForRetry(recipient),
              },
              'data-testid': `deliverability-toast-${ev.type.replace('email.', '')}`,
            });
          });
        }
        if (res.data?.server_now) {
          setServerNowCheckpoint(res.data.server_now);
        }
      } catch (err) {
        // Silent — a 5xx during a 30s poll shouldn't nag the user.
        logger.warn('Event poll failed', err);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [sow?.sow_id, serverNowCheckpoint, fetchSendHistory, openSendDialogForRetry]);

  const handleResend = async (sendId) => {
    if (!sow) return;
    setResendingId(sendId);
    try {
      const res = await axios.post(
        `${API_URL}/api/sow/${sow.sow_id}/send-history/${sendId}/resend`,
      );
      const n = (res.data?.attachments || []).length;
      toast.success(`SOW resent · ${n} attachment${n === 1 ? '' : 's'}`);
      fetchSendHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resend failed');
    } finally {
      setResendingId(null);
    }
  };

  const openSendDialog = () => {
    // Prefill: org contact email, default subject, empty message.
    setSendRecipientEmail(sow?.customer_contact_email || sow?.client_contact_email || '');
    setSendSubject(
      sow?.customer_legal_name
        ? `Statement of Work — ${sow.customer_legal_name}`
        : 'Statement of Work',
    );
    setSendMessage('');
    setSendDialogOpen(true);
  };

  const handleSendToClient = async () => {
    if (!sow) return;
    if (!sendRecipientEmail.trim()) {
      toast.error('Recipient email is required');
      return;
    }
    setSending(true);
    try {
      const companion = includeRawAppendix && appendixDelivery === 'companion';
      const res = await axios.post(
        `${API_URL}/api/sow/${sow.sow_id}/send-to-client`,
        {
          recipient_email: sendRecipientEmail.trim(),
          subject: sendSubject.trim(),
          message: sendMessage,
          include_raw_appendix: includeRawAppendix,
          appendix_delivery: appendixDelivery,
        },
      );
      const n = (res.data?.attachments || []).length;
      toast.success(
        companion
          ? `SOW + companion appendix emailed (${n} attachments)`
          : 'SOW emailed to client',
      );
      setSendDialogOpen(false);
      fetchData();
      fetchSendHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const [exporting, setExporting] = useState(false);
  // Pre-filled from SOW's resolved export defaults (user pref > org
  // default > false). Consultants can override per export.
  const [includeRawAppendix, setIncludeRawAppendix] = useState(false);
  // v379 — include the FX conversion basis line in the exported SOW.
  const [includeFx, setIncludeFx] = useState(true);
  // Delivery mode when the appendix is included: "embedded" folds it
  // into the SOW DOCX (default); "companion" downloads a clean SOW
  // plus a separate "SOW Appendix A — Fee Derivation.docx" working
  // paper, for clients whose legal teams prefer the signed instrument
  // and working papers to be distinct documents.
  const [appendixDelivery, setAppendixDelivery] = useState('embedded');
  useEffect(() => {
    if (sow?.export_defaults?.include_raw_appendix !== undefined) {
      setIncludeRawAppendix(Boolean(sow.export_defaults.include_raw_appendix));
    }
  }, [sow?.sow_id, sow?.export_defaults?.include_raw_appendix]);

  const downloadDocxResponse = (res, fallbackName) => {
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    const disposition = res.headers['content-disposition'];
    const filename = disposition
      ? disposition.split('filename=')[1]?.replace(/"/g, '')
      : fallbackName;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportDocx = async () => {
    if (!sow) return;
    setExporting(true);
    try {
      const companion = includeRawAppendix && appendixDelivery === 'companion';
      // Main SOW — if the appendix is requested as a companion file,
      // keep the main SOW clean; otherwise embed it.
      const res = await axios.get(
        `${API_URL}/api/sow/${sow.sow_id}/export/docx`,
        {
          responseType: 'blob',
          params: {
            include_raw_appendix: companion ? false : includeRawAppendix,
            include_fx: includeFx,
          },
        },
      );
      downloadDocxResponse(
        res,
        `Seertech_SOW_${sow.customer_legal_name || 'Document'}.docx`,
      );

      if (companion) {
        // Small staggered delay so browsers don't suppress the 2nd download.
        await new Promise(r => setTimeout(r, 400));
        const resAp = await axios.get(
          `${API_URL}/api/sow/${sow.sow_id}/export/appendix-docx`,
          { responseType: 'blob' },
        );
        downloadDocxResponse(
          resAp,
          `SOW_Appendix_A_Fee_Derivation_${sow.customer_legal_name || 'Document'}.docx`,
        );
        toast.success('SOW + companion appendix downloaded');
      } else {
        toast.success('SOW exported successfully');
      }
    } catch (err) {
      toast.error('Failed to export SOW');
      logger.error(err);
    } finally {
      setExporting(false);
    }
  };

  const updateField = (path, value) => {
    setForm(prev => {
      const parts = path.split('.');
      if (parts.length === 1) return { ...prev, [path]: value };
      const newForm = { ...prev };
      let obj = newForm;
      for (let i = 0; i < parts.length - 1; i++) {
        obj[parts[i]] = { ...obj[parts[i]] };
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return newForm;
    });
  };

  const addListItem = (field) => {
    setForm(prev => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const updateListItem = (field, index, value) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item),
    }));
  };

  const removeListItem = (field, index) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#4ABFCF]" />
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[sow?.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusCfg.icon;

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6" data-testid="sow-editor">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="sow-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">
              Statement of Work
            </h1>
            <p className="text-sm text-slate-500 flex items-center gap-2">
              <span>
                {estimate?.estimate_name || sow?.customer_legal_name || 'New SOW'}
                {estimate?.organization_name && ` — ${estimate.organization_name}`}
              </span>
              {estimate?.id && (
                <button
                  type="button"
                  onClick={() => window.open(`/estimator?load=${estimate.id}`, '_blank', 'noopener')}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 hover:bg-[#4ABFCF]/10 border border-slate-200 hover:border-[#4ABFCF]/40 text-[11px] font-medium text-slate-600 hover:text-[#0F2040] transition-colors"
                  title={`Open the source estimate (${estimate.estimate_name || estimate.id}${estimate.version ? ` · v${estimate.version}` : ''}) in a new tab — the exact components, conditions and rate-card that fed this SOW.`}
                  data-testid="sow-open-estimate-snapshot-btn"
                >
                  <ExternalLink className="w-3 h-3" />
                  Open Estimate snapshot
                  {estimate.version ? <span className="text-slate-400 font-mono">· v{estimate.version}</span> : null}
                </button>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {sow && (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${statusCfg.color}`} data-testid="sow-status-badge">
              <StatusIcon className="w-3.5 h-3.5" />
              {statusCfg.label}
            </span>
          )}
          {sow && (
            <div className="flex items-start gap-3 pr-1">
              <label
                className="inline-flex items-start gap-1.5 text-[11px] leading-tight cursor-pointer select-none"
                title="Appends a precise rate-card and fee derivation table (phase-split) — keeps the contract body's rounded fees while giving auditors one canonical raw breakdown."
                data-testid="sow-export-include-raw-wrapper"
              >
                <input
                  type="checkbox"
                  checked={includeRawAppendix}
                  onChange={(e) => setIncludeRawAppendix(e.target.checked)}
                  className="mt-0.5 accent-[#0F2040] cursor-pointer"
                  data-testid="sow-export-include-raw-checkbox"
                />
                <span className="text-slate-600 max-w-[140px]">
                  Raw rate-card &amp; fee appendix
                </span>
              </label>
              <label className="flex items-start gap-1.5 text-[11px] cursor-pointer" title="Adds a small 'Pricing basis' footer (entity · region · currency · FX snapshot) to the exported document">
                <input
                  type="checkbox"
                  checked={includeFx}
                  onChange={(e) => setIncludeFx(e.target.checked)}
                  className="mt-0.5 accent-[#0F2040] cursor-pointer"
                  data-testid="sow-export-include-fx-checkbox"
                />
                <span className="text-slate-600 max-w-[140px]">
                  FX conversion basis
                </span>
              </label>
              {includeRawAppendix && (
                <div
                  className="flex flex-col gap-0.5 text-[10px] text-slate-500 border-l border-slate-200 pl-2"
                  data-testid="sow-export-appendix-delivery"
                >
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input
                      type="radio"
                      name="sow-appendix-delivery"
                      value="embedded"
                      checked={appendixDelivery === 'embedded'}
                      onChange={() => setAppendixDelivery('embedded')}
                      className="accent-[#0F2040] cursor-pointer"
                      data-testid="sow-export-appendix-delivery-embedded"
                    />
                    <span>Embedded in SOW</span>
                  </label>
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input
                      type="radio"
                      name="sow-appendix-delivery"
                      value="companion"
                      checked={appendixDelivery === 'companion'}
                      onChange={() => setAppendixDelivery('companion')}
                      className="accent-[#0F2040] cursor-pointer"
                      data-testid="sow-export-appendix-delivery-companion"
                    />
                    <span>Companion file</span>
                  </label>
                </div>
              )}
            </div>
          )}
          {sow && templateInfo && (
            templateInfo.ok ? (
              <span
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-800 border border-emerald-200"
                title={`Modern Seertech SOW template available (${templateInfo.size_bytes ? (templateInfo.size_bytes / (1024 * 1024)).toFixed(1) + ' MB' : 'OK'}). Exports will use the modern format.`}
                data-testid="sow-template-ok-chip"
              >
                <ShieldCheck className="w-3 h-3" />
                Template OK
              </span>
            ) : (
              <button
                type="button"
                onClick={fetchTemplateInfo}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium bg-rose-50 text-rose-800 border border-rose-300 hover:bg-rose-100"
                title="Master SOW template is missing from this deployment. Exports will fall back to the legacy format. Re-deploy with backend/assets/Seertech_SOW_Template.docx bundled."
                data-testid="sow-template-missing-chip"
              >
                <ShieldAlert className="w-3 h-3" />
                Template missing
              </button>
            )
          )}
          {sow && (
            <Button size="sm" variant="outline" onClick={handleExportDocx} disabled={exporting} data-testid="sow-export-docx-btn">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
              Export DOCX
            </Button>
          )}
          {!isReadOnly && (
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#4ABFCF] hover:bg-[#3AAAB9]" data-testid="sow-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save Draft
            </Button>
          )}
          {canSubmit && sow && (
            <Button size="sm" onClick={handleSubmitForApproval} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="sow-submit-btn">
              <Send className="w-4 h-4 mr-1" /> Submit for Approval
            </Button>
          )}
          {canApprove && (
            <>
              <Button size="sm" onClick={handleApprove} className="bg-green-600 hover:bg-green-700" data-testid="sow-approve-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
              </Button>
              <Button size="sm" onClick={handleReject} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="sow-reject-btn">
                <XCircle className="w-4 h-4 mr-1" /> Request Changes
              </Button>
            </>
          )}
          {canSendToClient && (
            <Button size="sm" onClick={openSendDialog} className="bg-blue-600 hover:bg-blue-700" data-testid="sow-send-client-btn">
              <Send className="w-4 h-4 mr-1" /> Send to Client
            </Button>
          )}
        </div>
      </div>

      {/* Send-to-Client email dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="sow-send-dialog">
          <DialogHeader>
            <DialogTitle>Send SOW to Client</DialogTitle>
            <DialogDescription>
              The SOW DOCX will be attached and emailed via Seertech&apos;s
              Resend integration. {includeRawAppendix && appendixDelivery === 'companion'
                ? 'The Raw Rate-Card & Fee Derivation appendix will be attached as a separate companion file.'
                : includeRawAppendix
                  ? 'The Raw Rate-Card & Fee Derivation appendix is embedded in the SOW file.'
                  : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="sow-send-to" className="text-xs text-slate-500">
                Recipient email
              </Label>
              <Input
                id="sow-send-to"
                type="email"
                value={sendRecipientEmail}
                onChange={(e) => setSendRecipientEmail(e.target.value)}
                placeholder="client@example.com"
                data-testid="sow-send-recipient-input"
              />
            </div>
            <div>
              <Label htmlFor="sow-send-subject" className="text-xs text-slate-500">
                Subject
              </Label>
              <Input
                id="sow-send-subject"
                value={sendSubject}
                onChange={(e) => setSendSubject(e.target.value)}
                data-testid="sow-send-subject-input"
              />
            </div>
            <div>
              <Label htmlFor="sow-send-message" className="text-xs text-slate-500">
                Message (optional)
              </Label>
              <Textarea
                id="sow-send-message"
                rows={4}
                value={sendMessage}
                onChange={(e) => setSendMessage(e.target.value)}
                placeholder="Add a short note to the client…"
                data-testid="sow-send-message-input"
              />
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50/70 p-3 text-xs text-slate-600 space-y-1">
              <div className="font-semibold text-[#0F2040]">Attachments</div>
              <div>• Seertech_SOW_{(sow?.customer_legal_name || 'Document').replace(/ /g, '_')}.docx</div>
              {includeRawAppendix && appendixDelivery === 'companion' && (
                <div data-testid="sow-send-companion-attachment-line">
                  • SOW_Appendix_A_Fee_Derivation_{(sow?.customer_legal_name || 'Document').replace(/ /g, '_')}.docx
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSendDialogOpen(false)}
              disabled={sending}
              data-testid="sow-send-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSendToClient}
              disabled={sending || !sendRecipientEmail.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="sow-send-confirm-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval History */}
      {sow?.approvals?.length > 0 && (
        <div className="mb-6 bg-slate-50 rounded-lg p-4 border border-slate-200" data-testid="sow-approval-history">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Approval History</h3>
          {sow.approvals.map((a, i) => (
            <div key={`approval-${a.reviewed_at}-${i}`} className="flex items-center gap-2 text-sm text-slate-600">
              {a.status === 'approved' ? (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500" />
              )}
              <span className="font-medium">{a.reviewer_name}</span>
              <span>{a.status === 'approved' ? 'approved' : 'requested changes'}</span>
              {a.comments && <span className="text-slate-400">— "{a.comments}"</span>}
              <span className="text-slate-400 text-xs ml-auto">{new Date(a.reviewed_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}

      {/* Send History */}
      {sendHistory.length > 0 && (
        <div
          className="mb-6 bg-slate-50 rounded-lg p-4 border border-slate-200"
          data-testid="sow-send-history"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Send className="w-3.5 h-3.5 text-slate-400" /> Send History
              {unreadEventDelta > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setUnreadEventDelta(0);
                    seedEventCheckpoint();
                  }}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#4ABFCF] text-white border border-[#4ABFCF] hover:bg-[#0F2040] hover:border-[#0F2040] transition-colors animate-pulse"
                  data-testid="sow-send-history-new-events-pill"
                  title="Click to dismiss — new deliverability events have arrived"
                >
                  {unreadEventDelta} new event{unreadEventDelta === 1 ? '' : 's'}
                </button>
              )}
            </h3>
            <span className="text-[11px] text-slate-400">
              last {sendHistory.length} {sendHistory.length === 1 ? 'send' : 'sends'}
            </span>
          </div>
          <ul className="space-y-1.5">
            {sendHistory.map((s) => {
              const when = s.sent_at ? new Date(s.sent_at) : null;
              const whenLabel = when ? when.toLocaleString(undefined, {
                month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit',
              }) : '—';
              const attCount = (s.attachments || []).length;
              const statusMeta = s.status ? SEND_STATUS_CONFIG[s.status] : null;
              const StatusIcon = statusMeta?.icon;
              const isExpanded = expandedSendId === s.send_id;
              const rowEvents = (s.events || []).slice().reverse();
              return (
                <li
                  key={s.send_id}
                  className="bg-white rounded-md border border-slate-200"
                  data-testid={`sow-send-history-item-${s.send_id}`}
                >
                  <div className="flex items-center gap-3 text-xs px-3 py-1.5">
                    <button
                      type="button"
                      onClick={() => setExpandedSendId(isExpanded ? null : s.send_id)}
                      className="flex items-center gap-1 text-slate-400 hover:text-[#0F2040] transition-colors shrink-0"
                      data-testid={`sow-send-history-expand-${s.send_id}`}
                      aria-label={isExpanded ? 'Collapse event timeline' : 'Expand event timeline'}
                    >
                      {isExpanded
                        ? <ChevronDown className="w-3 h-3" />
                        : <ChevronRight className="w-3 h-3" />}
                    </button>
                    <Send className="w-3 h-3 text-[#4ABFCF] shrink-0" />
                    <span className="text-slate-700 font-medium truncate">
                      {s.recipient_email || '(unknown)'}
                    </span>
                    {statusMeta && StatusIcon && (
                      <span
                        className={`inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full border shrink-0 ${statusMeta.color}`}
                        data-testid={`sow-send-history-status-${s.send_id}`}
                        title={`Latest deliverability event: ${s.status} · ${s.last_event_at ? new Date(s.last_event_at).toLocaleString() : 'unknown time'}`}
                      >
                        <StatusIcon className="w-2.5 h-2.5" />
                        {statusMeta.label}
                      </span>
                    )}
                    <span className="text-slate-400 shrink-0">·</span>
                    <span className="text-slate-500 shrink-0">
                      {attCount} {attCount === 1 ? 'attachment' : 'attachments'}
                    </span>
                    {s.companion_appendix && (
                      <span
                        className="inline-flex items-center text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#4ABFCF]/10 text-[#0F2040] border border-[#4ABFCF]/30 shrink-0"
                        title="Raw appendix was sent as a separate companion file"
                      >
                        Companion
                      </span>
                    )}
                    {s.resent_from_send_id && (
                      <span
                        className="inline-flex items-center text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 shrink-0"
                        title={`Resend of ${s.resent_from_send_id}`}
                      >
                        Resend
                      </span>
                    )}
                    <span className="text-slate-400 text-[11px] ml-auto shrink-0">{whenLabel}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-[11px]"
                      onClick={() => handleResend(s.send_id)}
                      disabled={resendingId === s.send_id}
                      data-testid={`sow-send-history-resend-${s.send_id}`}
                    >
                      {resendingId === s.send_id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : 'Resend'}
                    </Button>
                  </div>
                  {isExpanded && (
                    <div
                      className="px-3 pb-2 pt-0 border-t border-slate-100 bg-slate-50/40"
                      data-testid={`sow-send-history-timeline-${s.send_id}`}
                    >
                      {rowEvents.length === 0 ? (
                        <p className="text-[10px] text-slate-400 italic py-1.5">
                          No deliverability events received yet — Resend will stream them in as the email progresses.
                        </p>
                      ) : (
                        <ol className="space-y-0.5 pt-1">
                          {rowEvents.map((ev, idx) => {
                            const at = ev.at ? new Date(ev.at) : null;
                            const atLabel = at ? at.toLocaleString(undefined, {
                              month: 'short', day: 'numeric',
                              hour: '2-digit', minute: '2-digit', second: '2-digit',
                            }) : '—';
                            const evStatus = (ev.type || '').replace('email.', '');
                            const evMeta = SEND_STATUS_CONFIG[evStatus];
                            const EvIcon = evMeta?.icon || Send;
                            return (
                              <li
                                key={`${s.send_id}-ev-${idx}`}
                                className="flex items-center gap-2 text-[10px] text-slate-600"
                              >
                                <EvIcon className={`w-2.5 h-2.5 shrink-0 ${evMeta ? '' : 'text-slate-400'}`} />
                                <span className="font-medium">{evMeta?.label || ev.type}</span>
                                <span className="text-slate-400">·</span>
                                <span className="text-slate-400">{atLabel}</span>
                              </li>
                            );
                          })}
                        </ol>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="flex gap-6">
        {/* Sidebar Navigation */}
        <div className="w-48 shrink-0">
          <nav className="space-y-1 sticky top-6">
            {SOW_SECTIONS.map(section => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                    activeSection === section.id
                      ? 'bg-[#4ABFCF]/10 text-[#4ABFCF] font-medium'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`sow-nav-${section.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {section.label}
                  {activeSection === section.id && <ChevronRight className="w-3 h-3 ml-auto" />}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {activeSection === 'overview' && (
            <SectionOverview form={form} updateField={updateField} isReadOnly={isReadOnly} estimate={estimate} seertechEntities={seertechEntities} organization={organization} userDefaultEntityId={user?.preferences?.default_contracting_entity_id} sowId={sow?.sow_id || null} onSyncConditionsDates={handleSyncConditionsDates} syncingDates={syncingDates} />
          )}
          {activeSection === 'integrations' && (
            <SectionIntegrations sow={sow} estimate={estimate} integrationTypes={integrationTypes} />
          )}
          {activeSection === 'migration' && (
            <SectionMigration form={form} updateField={updateField} isReadOnly={isReadOnly} />
          )}
          {activeSection === 'payment' && (
            <SectionPayment form={form} updateField={updateField} isReadOnly={isReadOnly}
              sow={sow} estimateCurrency={estimate?.currency_code} onCurrencyAction={doCurrencyAction} />
          )}
          {activeSection === 'assumptions' && (
            <SectionAssumptions form={form} updateField={updateField} addListItem={addListItem}
              updateListItem={updateListItem} removeListItem={removeListItem} isReadOnly={isReadOnly} />
          )}
          {activeSection === 'signatories' && (
            <SectionSignatories form={form} updateField={updateField} isReadOnly={isReadOnly} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Section Components ────────────────────────────────────────── */

function SectionCard({ title, children }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-6 mb-4">
      {title && <h2 className="text-base font-semibold text-slate-900 mb-4">{title}</h2>}
      {children}
    </div>
  );
}

function FieldRow({ label, children, hint }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function AutoBadge() {
  return <span className="ml-2 text-[10px] bg-[#4ABFCF]/10 text-[#4ABFCF] px-1.5 py-0.5 rounded font-medium">AUTO</span>;
}

function SectionOverview({ form, updateField, isReadOnly, estimate, seertechEntities = [], organization = null, userDefaultEntityId = null, sowId = null, onSyncConditionsDates, syncingDates = false }) {
  const selectedEntity = seertechEntities.find(e => e.id === form.contracting_entity_id);
  const userDefaultEntity = userDefaultEntityId
    ? seertechEntities.find(e => e.id === userDefaultEntityId)
    : null;
  const entityDrift = (
    !!userDefaultEntityId
    && !!form.contracting_entity_id
    && userDefaultEntityId !== form.contracting_entity_id
    && !!userDefaultEntity
  );

  // Mirror the backend precedence (sow_template_builder.py):
  //   preamble customer slot = Organization.legal_name
  //                            || estimate.org_name
  //                            || customer_legal_name
  //                            || "[Customer]"
  const previewCustomer = (
    (organization?.legal_name || '').trim() ||
    (estimate?.organization_name || '').trim() ||
    (form.customer_legal_name || '').trim() ||
    '[Customer]'
  );
  const previewEntity = (selectedEntity?.name || 'Seertech Solutions, Inc.').trim();
  const previewDate = (() => {
    const raw = form.sow_effective_date;
    if (!raw) return new Date().toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' });
    try {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return raw;
      return d.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' });
    } catch {
      return raw;
    }
  })();

  // Surface a warning when customer_legal_name differs from the Organization's
  // legal_name — the exported SOW will use the Organization record, which can
  // surprise the consultant who just edited the customer_legal_name field.
  const legalMismatch = (
    !!organization?.legal_name &&
    !!form.customer_legal_name &&
    organization.legal_name.trim().toLowerCase() !== form.customer_legal_name.trim().toLowerCase()
  );

  return (
    <>
      <SectionCard title="Customer Details">
        <div className="grid grid-cols-2 gap-4">
          <FieldRow label="Customer Legal Name">
            <Input value={form.customer_legal_name} onChange={e => updateField('customer_legal_name', e.target.value)}
              disabled={isReadOnly} placeholder="Legal entity name" data-testid="sow-customer-name" />
          </FieldRow>
          <FieldRow label="SOW Effective Date">
            <DatePickerField value={form.sow_effective_date || ''} onChange={(iso) => updateField('sow_effective_date', iso)}
              disabled={isReadOnly} testId="sow-effective-date" placeholder="Pick effective date" widthClass="w-full" />
          </FieldRow>
        </div>
        <FieldRow label="Customer Goal">
          <div className="space-y-1.5">
            <Textarea value={form.customer_goal} onChange={e => { updateField('customer_goal', e.target.value); updateField('goal_inherited_from_conditions', false); }}
              disabled={isReadOnly} placeholder="Describe the customer's primary goal for this implementation..."
              rows={3} data-testid="sow-customer-goal" />
            {form.goal_inherited_from_conditions && (
              <p className="text-[10px] text-slate-500 italic flex items-center gap-1" data-testid="sow-goal-inherited-pill">
                <Sparkles className="w-3 h-3 text-[#4ABFCF]" />
                Pre-filled from Estimate's Project Conditions. Edit freely — your changes won't be overwritten.
              </p>
            )}
          </div>
        </FieldRow>
      </SectionCard>

      <SectionCard title="Seertech Contracting Entity">
        <FieldRow
          label="Contracting Entity"
          hint="Determines the legal entity name &amp; registered address printed on the SOW preamble and signature block. Super Admins manage the list in Admin → Seertech Entities."
        >
          <Select
            value={form.contracting_entity_id || ''}
            onValueChange={v => updateField('contracting_entity_id', v)}
            disabled={isReadOnly || seertechEntities.length === 0}
          >
            <SelectTrigger data-testid="sow-contracting-entity-select">
              <SelectValue placeholder={seertechEntities.length ? 'Select entity' : 'No entities configured'} />
            </SelectTrigger>
            <SelectContent>
              {seertechEntities.map(e => (
                <SelectItem key={e.id} value={e.id} data-testid={`sow-contracting-entity-option-${e.id}`}>
                  {e.code ? `${e.code} \u2014 ${e.name}` : e.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldRow>
        {selectedEntity?.address && (
          <div className="mt-2 p-3 bg-slate-50 rounded-md text-xs text-slate-600 border" data-testid="sow-contracting-entity-address">
            <span className="font-medium text-slate-700">Registered Address:</span>{' '}
            {selectedEntity.address}
          </div>
        )}
        {entityDrift && (
          <div
            className="mt-2 flex items-center justify-between gap-3 p-2.5 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900"
            data-testid="sow-entity-drift-chip"
          >
            <span className="leading-snug">
              <span className="font-semibold">Differs from your default</span>
              {' — '}you prefer{' '}
              <span className="font-semibold">
                {userDefaultEntity?.code || userDefaultEntity?.name}
              </span>{' '}
              but this SOW uses{' '}
              <span className="font-semibold">
                {selectedEntity?.code || selectedEntity?.name || form.contracting_entity_id}
              </span>.
            </span>
            {!isReadOnly && (
              <button
                type="button"
                onClick={() => updateField('contracting_entity_id', userDefaultEntityId)}
                className="shrink-0 text-[11px] font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-700"
                data-testid="sow-entity-drift-sync-btn"
              >
                Sync to my default
              </button>
            )}
          </div>
        )}
      </SectionCard>

      {/* ── Live SOW Header Preview ────────────────────────────── */}
      <SectionCard title={<>Preview SOW Header <AutoBadge /></>}>
        <p className="text-[11px] text-slate-500 -mt-1 mb-2">
          Exactly what will appear on the exported SOW preamble &amp; signature —
          updates live as you tweak the fields above.
        </p>
        <div className="rounded-md border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 space-y-3"
             data-testid="sow-header-preview">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
            Executive Summary &mdash; Preamble
          </div>
          <p className="text-[13px] leading-6 text-slate-700 font-serif italic">
            This Statement of Work (this &ldquo;SOW&rdquo;) is entered into
            between{' '}
            <span className="not-italic font-semibold text-[#0F2040]" data-testid="preview-entity-name">
              {previewEntity}
            </span>{' '}
            (&ldquo;Company&rdquo; or &ldquo;Seertech&rdquo;) and{' '}
            <span className="not-italic font-semibold text-[#0F2040]" data-testid="preview-customer-name">
              {previewCustomer}
            </span>{' '}
            (&ldquo;Customer&rdquo;) as of{' '}
            <span className="not-italic font-semibold text-[#0F2040]" data-testid="preview-effective-date">
              {previewDate}
            </span>{' '}
            (the &ldquo;SOW Effective Date&rdquo;).
          </p>
          <div className="pt-2 border-t border-slate-200 space-y-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              Signature Block
            </div>
            <div className="grid grid-cols-2 gap-6 text-[11px] font-mono">
              <div className="text-slate-700 font-semibold tracking-wide" data-testid="preview-signature-customer">
                {previewCustomer.toUpperCase()}
              </div>
              <div className="text-slate-700 font-semibold tracking-wide" data-testid="preview-signature-entity">
                {previewEntity.toUpperCase()}
              </div>
            </div>
            {selectedEntity?.address && (
              <div className="text-[10px] text-slate-500 pt-1" data-testid="preview-signature-entity-address">
                {previewEntity} &bull; {selectedEntity.address}
              </div>
            )}
          </div>
          {legalMismatch && (
            <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900"
                 data-testid="sow-legal-name-mismatch-warning">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" />
              <div>
                <span className="font-semibold">Heads up:</span>{' '}
                You typed &ldquo;{form.customer_legal_name}&rdquo; above, but the
                Organization record stores the legal name as{' '}
                &ldquo;{organization.legal_name}&rdquo; &mdash; the exported SOW
                will use the Organization&rsquo;s legal name. Update the
                Organization in the Organizations page if that&rsquo;s wrong.
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Project Timeline">
        {estimate?.has_conditions && estimate?.project_conditions && (estimate.project_conditions.goal_start_date || estimate.project_conditions.goal_go_live_date) && (() => {
          const pcStart = estimate.project_conditions.goal_start_date || '';
          const pcGoLive = estimate.project_conditions.goal_go_live_date || '';
          const sowStart = form.start_date || '';
          const sowGoLive = form.go_live_date || '';
          const startDrift = pcStart && sowStart && pcStart !== sowStart;
          const golDrift = pcGoLive && sowGoLive && pcGoLive !== sowGoLive;
          const anyDrift = startDrift || golDrift;
          const tone = anyDrift
            ? 'bg-amber-50 ring-amber-300 text-amber-900'
            : 'bg-[#4ABFCF]/10 ring-[#4ABFCF]/30 text-[#0F2040]';
          const iconTone = anyDrift ? 'text-amber-600' : 'text-[#4ABFCF]';
          return (
            <div
              className={`mb-3 px-3 py-2 rounded-md ring-1 text-xs flex items-start gap-2 ${tone}`}
              data-testid="sow-conditions-prefill-notice"
            >
              {anyDrift
                ? <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${iconTone}`} />
                : <Lock className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${iconTone}`} />}
              <div className="flex-1">
                <span className="font-semibold">
                  {anyDrift ? 'Project Conditions have changed since this SOW was created:' : 'Pre-filled from Project Conditions:'}
                </span>{' '}
                {pcStart && <span>Start <strong>{pcStart}</strong>. </span>}
                {pcGoLive && <span>Goal Go-Live <strong>{pcGoLive}</strong>. </span>}
                {anyDrift
                  ? <span>This SOW currently shows {sowStart && <strong>{sowStart}</strong>}{sowStart && sowGoLive ? ' / ' : ''}{sowGoLive && <strong>{sowGoLive}</strong>} — click Sync to refresh, or keep your manual override.</span>
                  : <span>You can override here for SOW purposes — changes won&rsquo;t affect the Estimator.</span>}
              </div>
              {anyDrift && !isReadOnly && sowId && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={onSyncConditionsDates}
                  disabled={syncingDates}
                  className="h-7 text-[11px] border-amber-400 text-amber-800 hover:bg-amber-100"
                  data-testid="sow-sync-conditions-dates-btn"
                >
                  {syncingDates
                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    : <RefreshCw className="w-3 h-3 mr-1" />}
                  Sync to current Conditions
                </Button>
              )}
            </div>
          );
        })()}
        <div className="grid grid-cols-2 gap-4">
          <FieldRow label="Start Date">
            <DatePickerField value={form.start_date || ''} onChange={(iso) => updateField('start_date', iso)}
              disabled={isReadOnly} testId="sow-start-date" placeholder="Pick start date" widthClass="w-full" />
          </FieldRow>
          <FieldRow label="Goal Go-Live Date">
            <DatePickerField value={form.go_live_date || ''} onChange={(iso) => updateField('go_live_date', iso)}
              disabled={isReadOnly} testId="sow-go-live-date" placeholder="Pick go-live date" widthClass="w-full"
              minDate={form.start_date || undefined} anchorDate={form.start_date || undefined} quickRanges />
          </FieldRow>
        </div>
        {estimate && (
          <div className="mt-3 p-3 bg-slate-50 rounded-md text-sm text-slate-600">
            <span className="font-medium">From Estimate:</span>{' '}
            {estimate.implementation_type} — {estimate.complexity} complexity —{' '}
            {Math.round(estimate.results?.total_effort || 0).toLocaleString()} hours estimated
          </div>
        )}
      </SectionCard>

      <SectionCard title="Implementation Type">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-slate-600">
            <Building2 className="w-4 h-4 inline mr-1" />
            {estimate?.implementation_type || 'N/A'}
          </span>
          <span className="text-slate-400">|</span>
          <span className="text-slate-600">Complexity: {estimate?.complexity || 'N/A'}</span>
          <span className="text-slate-400">|</span>
          <span className="text-slate-600">Hosting: {estimate?.hosting_model?.replace('_', ' ') || 'N/A'}</span>
          <AutoBadge />
        </div>
      </SectionCard>

      {/* Project Milestones preview — pulls the same milestones the
          DOCX export will render. Highlights the Hypercare row when an
          override (SOW-level or Super-Admin-level) is in effect. */}
      {sowId && (
        <MilestonesPreviewCard
          sowId={sowId}
          form={form}
          updateField={updateField}
          isReadOnly={isReadOnly}
        />
      )}
    </>
  );
}

function MilestonesPreviewCard({ sowId, form, updateField, isReadOnly }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // The preview call is debounced against `form.hypercare_duration_days_override`
  // so the banner + Hypercare date update as the user tweaks the field.
  const sowOverride = form?.hypercare_duration_days_override ?? null;
  const enabled = sowOverride !== null && sowOverride !== undefined && Number(sowOverride) > 0;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API_URL}/api/sow/${sowId}/milestones-preview`, { withCredentials: true })
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // Re-fetch whenever the persisted per-SOW override changes on save —
    // but we also preview the unsaved value locally below. `sow?.status`
    // changes (pending_cco → approved) also invalidate the preview.
  }, [sowId]);

  if (loading) return null;
  const serverMilestones = data?.milestones || [];

  // The override control stays visible even when we couldn't compute any
  // milestones (e.g., the linked estimate was deleted) so the user can
  // still toggle the per-SOW window. The preview table only renders when
  // milestones are available.
  const hasMilestones = serverMilestones.length > 0;

  // Re-compute locally when the user has changed the override in-form but
  // hasn't saved yet, so the preview updates live. We only adjust the
  // Hypercare Complete milestone — all others are unaffected.
  const milestones = serverMilestones.map(m => {
    if (!enabled) return m;
    const isHC = m.name === 'Hypercare Complete' || m.name === 'Project End';
    if (!isHC) return m;
    const goLive = serverMilestones.find(x => x.name === 'Go Live');
    if (!goLive?.date) return m;
    try {
      const gl = new Date(goLive.date);
      const shifted = new Date(gl.getTime() + Number(sowOverride) * 86400000);
      const iso = shifted.toISOString().slice(0, 10);
      return {
        ...m,
        name: 'Hypercare Complete',
        date: iso,
        duration_days_post_go_live: Number(sowOverride),
        week: (goLive.week || m.week) + Math.max(1, Math.round(Number(sowOverride) / 7)),
      };
    } catch { return m; }
  });

  const activeOverrideDays = enabled ? Number(sowOverride) : (data?.override_days || null);
  const overrideSource = enabled ? 'sow' : (data?.override_source || null);

  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    } catch { return iso; }
  };

  return (
    <SectionCard title="Project Milestones Preview">
      {/* Per-SOW Hypercare override control */}
      <div
        className="mb-4 p-3 rounded-md border border-slate-200 bg-slate-50"
        data-testid="sow-hypercare-override-control"
      >
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="w-4 h-4 accent-[#4ABFCF]"
            checked={enabled}
            disabled={isReadOnly}
            onChange={e => {
              if (!e.target.checked) {
                updateField('hypercare_duration_days_override', null);
              } else {
                // Seed with the admin default (from data.override_days) when
                // available, else 30 days — a sensible industry default.
                const seed = (data?.override_source === 'admin' && data?.override_days)
                  ? data.override_days
                  : 30;
                updateField('hypercare_duration_days_override', seed);
              }
            }}
            data-testid="sow-hypercare-override-toggle"
          />
          <span className="text-xs font-semibold text-[#0F2040]">
            Custom Hypercare window for this SOW
          </span>
        </label>
        {enabled && (
          <div className="mt-2 space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={365}
                step={1}
                value={Number(sowOverride)}
                disabled={isReadOnly}
                onChange={e => {
                  const n = parseInt(e.target.value, 10);
                  updateField(
                    'hypercare_duration_days_override',
                    Number.isFinite(n) && n > 0 ? n : 1,
                  );
                }}
                className="w-24 border border-slate-300 rounded px-2 py-1 text-sm"
                data-testid="sow-hypercare-override-days"
              />
              <span className="text-xs text-slate-600">days post Go-Live</span>
              <span className="ml-auto text-[10px] text-slate-500">
                Overrides the Super Admin planner default on this SOW only.
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5" data-testid="sow-hypercare-preset-chips">
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mr-0.5">Quick set:</span>
              {[15, 30, 45, 60, 90].map((preset) => {
                const active = Number(sowOverride) === preset;
                return (
                  <button
                    key={preset}
                    type="button"
                    disabled={isReadOnly}
                    onClick={() => updateField('hypercare_duration_days_override', preset)}
                    className={`text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${
                      active
                        ? 'bg-[#0F2040] text-white border-[#0F2040]'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-[#4ABFCF] hover:text-[#0F2040]'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    data-testid={`sow-hypercare-preset-${preset}`}
                    aria-pressed={active}
                  >
                    {preset}d
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {!enabled && data?.override_source === 'admin' && (
          <p className="mt-1 text-[10px] text-slate-500">
            Currently inheriting the Super Admin default ({data.override_days} days).
            Tick the box to override for this SOW only.
          </p>
        )}
      </div>

      <div className="text-[11px] text-slate-500 mb-3 leading-snug">
        These are the milestones that will render in Section 7.3 of the exported SOW.
        {activeOverrideDays ? (
          <span className="ml-1 text-amber-800 font-medium" data-testid="milestone-override-banner">
            · Hypercare Complete is forced to Go-Live + {activeOverrideDays} days
            {overrideSource === 'sow' ? ' (per-SOW override)' : ' (Super Admin default)'}.
          </span>
        ) : null}
      </div>
      {hasMilestones ? (
        <div className="rounded-md border border-slate-200 divide-y divide-slate-100"
             data-testid="milestones-preview-table">
          {milestones.map((m, idx) => {
            const isHypercare = m.name === 'Hypercare Complete' || m.name === 'Project End';
            const isGoLive = m.name === 'Go Live';
            return (
              <div
                key={`${m.name}-${idx}`}
                className={`grid grid-cols-[1fr_auto_auto] items-center gap-3 px-3 py-1.5 text-[12px] ${
                  isHypercare && activeOverrideDays ? 'bg-amber-50' : ''
                }`}
                data-testid={`milestone-row-${m.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <span className={`${isGoLive ? 'font-semibold text-[#0F2040]' : 'text-slate-700'}`}>
                  {m.name}
                  {isHypercare && m.duration_days_post_go_live ? (
                    <span className="ml-1 text-[10px] text-amber-800">
                      (Go-Live + {m.duration_days_post_go_live}d)
                    </span>
                  ) : null}
                </span>
                <span className="text-[10px] text-slate-500">Week {m.week ?? '—'}</span>
                <span className="text-slate-700 font-mono text-[11px]">{fmt(m.date)}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-[11px] text-slate-500 italic" data-testid="milestones-unavailable">
          Milestone preview unavailable (no linked estimate or timeline could not be computed).
        </p>
      )}
    </SectionCard>
  );
}

function SectionIntegrations({ sow, estimate, integrationTypes }) {
  const integrationsFromEstimate = estimate?.integrations || sow?.integrations || [];
  const enabledIntegrations = integrationsFromEstimate.filter(i => i.enabled);

  return (
    <>
      <SectionCard title={<>Integrations in Scope <AutoBadge /></>}>
        <p className="text-sm text-slate-500 mb-4">
          Integrations are captured during estimate creation and automatically included in the SOW.
        </p>
        {enabledIntegrations.length > 0 ? (
          <div className="grid grid-cols-2 gap-2">
            {enabledIntegrations.map((intg, i) => (
              <div key={`intg-${intg.type || i}`} className="flex items-center gap-2 p-2.5 bg-green-50 rounded-md border border-green-200">
                <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                <div>
                  <span className="text-sm font-medium text-slate-700">{intg.label}</span>
                  {intg.notes && <p className="text-xs text-slate-500">{intg.notes}</p>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-slate-400 italic p-4 text-center bg-slate-50 rounded-md">
            No integrations selected. Add integrations in the Estimator to include them here.
          </div>
        )}
      </SectionCard>

      <SectionCard title="All Available Integration Types">
        <div className="grid grid-cols-3 gap-2 text-sm">
          {integrationTypes.map(t => {
            const isEnabled = enabledIntegrations.some(i => i.type === t.type);
            return (
              <div key={t.type} className={`flex items-center gap-2 p-2 rounded-md ${isEnabled ? 'bg-green-50 text-green-700' : 'bg-slate-50 text-slate-400'}`}>
                {isEnabled ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                {t.label}
              </div>
            );
          })}
        </div>
      </SectionCard>
    </>
  );
}

function SectionMigration({ form, updateField, isReadOnly }) {
  const dm = form.data_migration;
  const dataTypes = [
    { key: 'organizations', label: 'Organizations' },
    { key: 'users', label: 'Users' },
    { key: 'content_library', label: 'Content Library' },
    { key: 'learner_history', label: 'Learner History' },
    { key: 'certifications', label: 'Certifications' },
  ];

  return (
    <SectionCard title="Data Migration">
      <div className="grid grid-cols-3 gap-4 mb-4">
        <FieldRow label="Source System">
          <Input value={dm.source} onChange={e => updateField('data_migration.source', e.target.value)}
            disabled={isReadOnly} placeholder="e.g., Legacy LMS, HRIS" data-testid="sow-dm-source" />
        </FieldRow>
        <FieldRow label="Migration Date">
          <Input value={dm.date} onChange={e => updateField('data_migration.date', e.target.value)}
            disabled={isReadOnly} placeholder="e.g., TBD during Discovery" data-testid="sow-dm-date" />
        </FieldRow>
        <FieldRow label="Data Format">
          <Input value={dm.data_format} onChange={e => updateField('data_migration.data_format', e.target.value)}
            disabled={isReadOnly} placeholder="e.g., CSV, XML, API" data-testid="sow-dm-format" />
        </FieldRow>
      </div>
      <h3 className="text-sm font-medium text-slate-700 mb-3">Data Types to Migrate</h3>
      <div className="grid grid-cols-2 gap-2">
        {dataTypes.map(dt => (
          <label key={dt.key} className="flex items-center gap-2 p-2.5 rounded-md border cursor-pointer hover:bg-slate-50 transition-colors">
            <input
              type="checkbox"
              checked={dm[dt.key] || false}
              onChange={e => updateField(`data_migration.${dt.key}`, e.target.checked)}
              disabled={isReadOnly}
              className="rounded border-slate-300 text-[#4ABFCF] focus:ring-[#4ABFCF]"
              data-testid={`sow-dm-${dt.key}`}
            />
            <span className="text-sm text-slate-700">{dt.label}</span>
          </label>
        ))}
      </div>
    </SectionCard>
  );
}

function SectionPayment({ form, updateField, isReadOnly, sow, estimateCurrency, onCurrencyAction }) {
  const pt = form.payment_terms;
  const currency = (pt.currency || form.currency_code || 'USD').toUpperCase();
  const estCcy = (estimateCurrency || '').toUpperCase();
  const drift = !!estCcy && estCcy !== currency;
  const locked = !!sow?.fx_locked;
  const feeDisplay = `${currency} ${getCurrencySymbol(currency)}${(pt.total_fee || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const hoursDisplay = `${Math.round(pt.total_hours || 0).toLocaleString()} hours`;
  const scheduleLines = (() => {
    if (pt.schedule_type === '50_50' || !pt.schedule_type) {
      return [
        '50% of the one-time implementation fees are due upon the Effective Date / Commence Date of this agreement.',
        'The remaining 50% of the one-time implementation fees are due 90 days from the Welcome / Kick-Off Date.',
      ];
    }
    const label = pt.schedule_type === 'milestone' ? 'Milestone-based schedule' : 'Custom schedule';
    const details = (pt.schedule_details || '').trim();
    return details
      ? [`${label}:`, details]
      : [`${label} — add schedule details above to render here.`];
  })();

  return (
    <SectionCard title="Payment & Compensation">
      {sow?.sow_id && (
        <div className={`mb-4 rounded-lg border px-3 py-2.5 ${locked ? 'border-amber-300 bg-amber-50/50' : drift ? 'border-rose-200 bg-rose-50/40' : 'border-slate-200 bg-slate-50/60'}`}
          data-testid="sow-currency-sync-panel">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-semibold text-[#0F2040]">Payment currency: {currency}</span>
            {locked && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5" data-testid="sow-currency-locked-badge">
                Locked — price fixed at a point in time
              </span>
            )}
            {drift && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-rose-700 bg-rose-100 border border-rose-200 rounded px-1.5 py-0.5" data-testid="sow-currency-drift-badge">
                Estimate is now {estCcy}
              </span>
            )}
            {!drift && !locked && <span className="text-[10px] text-slate-400">In sync with the estimate — auto-updates while editable</span>}
            <div className="flex-1" />
            {!isReadOnly && (
              <div className="flex items-center gap-1.5">
                <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]"
                  onClick={() => onCurrencyAction && onCurrencyAction('lock', !locked)}
                  data-testid="sow-currency-lock-btn">
                  {locked ? 'Unlock currency' : 'Lock currency'}
                </Button>
                <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]"
                  disabled={!drift && !locked}
                  onClick={() => onCurrencyAction && onCurrencyAction('resync')}
                  data-testid="sow-currency-resync-btn">
                  Re-sync from estimate
                </Button>
                {sow?.pre_sync_currency && (
                  <Button type="button" size="sm" variant="ghost" className="h-7 text-[11px] text-slate-500"
                    onClick={() => onCurrencyAction && onCurrencyAction('revert')}
                    data-testid="sow-currency-revert-btn">
                    Revert to {sow.pre_sync_currency}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <FieldRow label={<>Total Implementation Fee <AutoBadge /></>} hint="Auto-populated from estimate">
          <div className="text-2xl font-bold text-slate-900" data-testid="sow-total-fee">
            {getCurrencySymbol(currency)}{(pt.total_fee || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </div>
        </FieldRow>
        <FieldRow label={<>Total Hours <AutoBadge /></>} hint="Auto-populated from estimate">
          <div className="text-2xl font-bold text-slate-900" data-testid="sow-total-hours">
            {Math.round(pt.total_hours || 0).toLocaleString()} hrs
          </div>
        </FieldRow>
      </div>
      <FieldRow label="Payment Schedule">
        <Select value={pt.schedule_type} onValueChange={v => updateField('payment_terms.schedule_type', v)} disabled={isReadOnly}>
          <SelectTrigger data-testid="sow-schedule-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="50_50">50/50 Split (Start / Go-Live)</SelectItem>
            <SelectItem value="milestone">Milestone-Based</SelectItem>
            <SelectItem value="custom">Custom Schedule</SelectItem>
          </SelectContent>
        </Select>
      </FieldRow>
      {pt.schedule_type !== '50_50' && (
        <FieldRow label="Schedule Details">
          <Textarea value={pt.schedule_details} onChange={e => updateField('payment_terms.schedule_details', e.target.value)}
            disabled={isReadOnly} placeholder="Describe the payment milestones or custom schedule..."
            rows={3} data-testid="sow-schedule-details" />
        </FieldRow>
      )}
      <FieldRow
        label="Include Milestone Payment Schedule in Exported SOW"
        hint="When off, the 'Milestone | Payment' table (e.g., 50% on Signature / 50% on Go-Live) is removed from the Word SOW. Milestone schedules are non-standard.">
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="sow-milestone-schedule-toggle">
          <input
            type="checkbox"
            checked={pt.include_milestone_schedule !== false}
            onChange={e => updateField('payment_terms.include_milestone_schedule', e.target.checked)}
            disabled={isReadOnly}
            className="h-4 w-4 accent-[#0F2040] rounded border-slate-300"
          />
          <span className="text-sm text-slate-700">{pt.include_milestone_schedule !== false ? 'Included' : 'Excluded'}</span>
        </label>
      </FieldRow>

      {/* ── Live Fees Line Preview ────────────────────────────── */}
      <div className="mt-4 pt-4 border-t border-slate-200">
        <div className="flex items-center gap-1.5 mb-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Preview Fees Sentence
          </p>
          <AutoBadge />
        </div>
        <p className="text-[11px] text-slate-500 -mt-1 mb-2">
          Exactly what will appear in the Total Implementation Fees section of the exported SOW.
        </p>
        <div className="rounded-md border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 space-y-3"
             data-testid="sow-fees-preview">
          <p className="text-[13px] leading-6 text-slate-700 font-serif italic">
            Customer will be charged a{' '}
            <span className="not-italic font-semibold text-[#0F2040]" data-testid="preview-fee-amount">
              {feeDisplay}
            </span>{' '}
            one-time implementation fee for services (the &ldquo;Services Fees&rdquo;)
            and set up, as long as scope does not change, change requests are not
            invoked, and the Go-Live date does not extend.
          </p>
          <p className="text-[12px] text-slate-600 italic">
            This price includes all services described in this SOW, covering{' '}
            <span className="not-italic font-semibold text-[#0F2040]" data-testid="preview-fee-hours">
              {hoursDisplay}
            </span>{' '}
            of implementation effort.
          </p>
          <div className="pt-2 border-t border-slate-200">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-1.5">
              Invoice Schedule
            </div>
            <ul className="text-[12px] text-slate-700 space-y-1 list-disc list-inside" data-testid="preview-fee-schedule">
              {scheduleLines.map((line, i) => (
                <li key={`sched-${i}-${line.slice(0, 24)}`} className="leading-5">{line}</li>
              ))}
            </ul>
          </div>
          {pt.include_milestone_schedule === false && (
            <div className="text-[10px] text-slate-500 italic" data-testid="preview-fee-milestone-excluded">
              Note: the &ldquo;Milestone | Payment&rdquo; table is excluded from this export
              (toggle above).
            </div>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

function SectionAssumptions({ form, addListItem, updateListItem, removeListItem, isReadOnly }) {
  return (
    <>
      <SectionCard title="Assumptions">
        <div className="space-y-2 mb-3">
          {form.assumptions.map((item, i) => (
            <div key={`assumption-${i}`} className="flex items-start gap-2">
              <span className="text-xs text-slate-400 mt-2.5 w-5 shrink-0">{i + 1}.</span>
              <Input value={item} onChange={e => updateListItem('assumptions', i, e.target.value)}
                disabled={isReadOnly} className="flex-1 text-sm" data-testid={`sow-assumption-${i}`} />
              {!isReadOnly && (
                <Button variant="ghost" size="sm" onClick={() => removeListItem('assumptions', i)} className="text-slate-400 hover:text-red-500 shrink-0 h-9 w-9 p-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
        {!isReadOnly && (
          <Button variant="outline" size="sm" onClick={() => addListItem('assumptions')} data-testid="sow-add-assumption">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Assumption
          </Button>
        )}
      </SectionCard>

      <SectionCard title="Out of Scope">
        <div className="space-y-2 mb-3">
          {form.out_of_scope.map((item, i) => (
            <div key={`oos-${i}`} className="flex items-start gap-2">
              <span className="text-xs text-slate-400 mt-2.5 w-5 shrink-0">{i + 1}.</span>
              <Input value={item} onChange={e => updateListItem('out_of_scope', i, e.target.value)}
                disabled={isReadOnly} className="flex-1 text-sm" data-testid={`sow-oos-${i}`} />
              {!isReadOnly && (
                <Button variant="ghost" size="sm" onClick={() => removeListItem('out_of_scope', i)} className="text-slate-400 hover:text-red-500 shrink-0 h-9 w-9 p-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
        {!isReadOnly && (
          <Button variant="outline" size="sm" onClick={() => addListItem('out_of_scope')} data-testid="sow-add-oos">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Item
          </Button>
        )}
      </SectionCard>
    </>
  );
}

function SectionSignatories({ form, updateField, isReadOnly }) {
  return (
    <>
      <SectionCard title="Customer Signatory">
        <div className="grid grid-cols-2 gap-4">
          <FieldRow label="Name">
            <Input value={form.customer_signatory.name} onChange={e => updateField('customer_signatory.name', e.target.value)}
              disabled={isReadOnly} placeholder="Full name" data-testid="sow-cust-sign-name" />
          </FieldRow>
          <FieldRow label="Title">
            <Input value={form.customer_signatory.title} onChange={e => updateField('customer_signatory.title', e.target.value)}
              disabled={isReadOnly} placeholder="Job title" data-testid="sow-cust-sign-title" />
          </FieldRow>
        </div>
      </SectionCard>

      <SectionCard title="Seertech Signatory">
        <div className="grid grid-cols-2 gap-4">
          <FieldRow label="Name">
            <Input value={form.seertech_signatory.name} onChange={e => updateField('seertech_signatory.name', e.target.value)}
              disabled={isReadOnly} placeholder="Full name" data-testid="sow-seer-sign-name" />
          </FieldRow>
          <FieldRow label="Title">
            <Input value={form.seertech_signatory.title} onChange={e => updateField('seertech_signatory.title', e.target.value)}
              disabled={isReadOnly} placeholder="Job title" data-testid="sow-seer-sign-title" />
          </FieldRow>
        </div>
      </SectionCard>
    </>
  );
}
